import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Anchor, Box, Divider, Group, Skeleton, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import {
  IconAlertTriangle,
  IconCopy,
  IconExternalLink,
  IconRefresh,
  IconTrash,
  IconUpload,
  IconWorldWww,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StyledTextarea from '@/components/StyledInputFields/StyledTextarea.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import useRetrieveAutomationPortal from '@/hooks/api/automationPortals/useRetrieveAutomationPortal.ts';
import useRetrieveAutomationPortalRuns from '@/hooks/api/automationPortals/useRetrieveAutomationPortalRuns.ts';
import useUpdateAutomationPortal from '@/hooks/api/automationPortals/useUpdateAutomationPortal.ts';
import useRegenerateAutomationPortalLink from '@/hooks/api/automationPortals/useRegenerateAutomationPortalLink.ts';
import useUploadAutomationPortalLogo from '@/hooks/api/automationPortals/useUploadAutomationPortalLogo.ts';
import useRemoveAutomationPortalLogo from '@/hooks/api/automationPortals/useRemoveAutomationPortalLogo.ts';
import { Automation, AutomationPortalRunSummary } from '@/types/automations.ts';
import { formatCalendarStyle } from '@/utils/datetime.ts';

const PORTAL_TITLE_MAX_LENGTH = 255;
const PORTAL_DESCRIPTION_MAX_LENGTH = 1024;
const PORTAL_RUN_ERROR_MESSAGE_MAX_LENGTH = 1024;
const PORTAL_SLUG_MAX_LENGTH = 64;
const PORTAL_SLUG_PATTERN = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;
const PORTAL_TOKEN_PATTERN = /^([a-z0-9]{16}|[a-z0-9]{4}(?:-[a-z0-9]{4}){3})$/;
const PORTAL_SLUG_VALIDATION_MESSAGE =
  'Use 3-64 letters, numbers, and single hyphens. Start with a letter and end with a letter or number.';
const PORTAL_RESERVED_SLUGS = new Set([
  'admin',
  'api',
  'automations',
  'login',
  'logout',
  'new',
  'projects',
  'runs',
  'settings',
]);

type AutomationPortalSettingsPanelProps = {
  automation: Automation;
  shouldLoad?: boolean;
};

const buildPublicPortalUrl = (portalIdentifier: string) => {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `${origin}/portals/${portalIdentifier}`;
};

const normalizePublicSlug = (value: string) => {
  const normalizedSlug = value.trim().toLowerCase();
  return normalizedSlug || null;
};

const getPublicSlugValidationError = (value: string) => {
  const normalizedSlug = normalizePublicSlug(value);
  if (!normalizedSlug) return null;

  if (
    !PORTAL_SLUG_PATTERN.test(normalizedSlug) ||
    normalizedSlug.includes('--') ||
    PORTAL_RESERVED_SLUGS.has(normalizedSlug) ||
    PORTAL_TOKEN_PATTERN.test(normalizedSlug)
  ) {
    return PORTAL_SLUG_VALIDATION_MESSAGE;
  }

  return null;
};

const AutomationPortalRunRow = ({
  automationId,
  portalRun,
}: {
  automationId: string;
  portalRun: AutomationPortalRunSummary;
}) => (
  <Group
    justify="space-between"
    align="center"
    gap="md"
    wrap="nowrap"
    py={8}
    style={{ borderTop: '1px solid #F1F1F3' }}
  >
    <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
      <Group gap="xs" wrap="nowrap">
        <StatusIndicator status={portalRun.status} include_text={false} />
        <Anchor
          component={Link}
          to={`/automations/${automationId}/runs/${portalRun.run_id}`}
          size="12px"
          fw={500}
          c="#0F172A"
          truncate="end"
        >
          {portalRun.run_name}
        </Anchor>
      </Group>
      <Text size="11px" c="#757575" truncate>
        {formatCalendarStyle(portalRun.created_at)}
        {portalRun.preprocessing_error ? ` · ${portalRun.preprocessing_error}` : ''}
      </Text>
    </Stack>
    <Anchor
      component={Link}
      to={`/automations/${automationId}/runs/${portalRun.run_id}`}
      size="11px"
      c="#5E6AD2"
      style={{ flexShrink: 0 }}
    >
      View
    </Anchor>
  </Group>
);

export const AutomationPortalSettingsPanel = ({
  automation,
  shouldLoad = true,
}: AutomationPortalSettingsPanelProps) => {
  const { data: portal, isLoading } = useRetrieveAutomationPortal(
    shouldLoad ? automation.id : undefined,
  );
  const { data: portalRuns, isLoading: isLoadingPortalRuns } = useRetrieveAutomationPortalRuns(
    shouldLoad ? automation.id : undefined,
    { limit: 10 },
  );
  const { mutate: updatePortal, isPending: isUpdatingPortal } = useUpdateAutomationPortal();
  const { mutate: regenerateLink, isPending: isRegeneratingLink } =
    useRegenerateAutomationPortalLink();
  const { mutate: uploadLogo, isPending: isUploadingLogo } = useUploadAutomationPortalLogo();
  const { mutate: removeLogo, isPending: isRemovingLogo } = useRemoveAutomationPortalLogo();

  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState(automation.name);
  const [description, setDescription] = useState('');
  const [runErrorMessage, setRunErrorMessage] = useState('');
  const [publicSlug, setPublicSlug] = useState('');
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  const publicUrlIdentifier = portal?.public_slug || portal?.public_token || '';
  const publicUrl = useMemo(
    () => (publicUrlIdentifier ? buildPublicPortalUrl(publicUrlIdentifier) : ''),
    [publicUrlIdentifier],
  );
  const publicSlugValidationError = getPublicSlugValidationError(publicSlug);

  const canSave =
    !!portal &&
    title.trim().length > 0 &&
    !publicSlugValidationError &&
    (!enabled || portal.is_ready);
  const hasLogo = !!portal?.logo_blob_path;
  const hasPublicSlug = !!portal?.public_slug;
  const recentPortalRuns = portalRuns?.results ?? [];

  useEffect(() => {
    if (!shouldLoad || !portal) return;

    setEnabled(portal.enabled);
    setTitle(portal.title || automation.name);
    setDescription(portal.description || '');
    setRunErrorMessage(portal.run_error_message || '');
    setPublicSlug(portal.public_slug || '');
  }, [automation.name, portal, shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) {
      setConfirmRegenerateOpen(false);
    }
  }, [shouldLoad]);

  const handleCopyUrl = async () => {
    if (!publicUrl) return;

    try {
      await navigator.clipboard.writeText(publicUrl);
      notifications.show({ message: 'Portal link copied', color: 'blue' });
    } catch {
      notifications.show({ message: 'Could not copy the portal link', color: 'red' });
    }
  };

  const handleSave = () => {
    if (!canSave) return;

    updatePortal(
      {
        automationId: automation.id,
        enabled,
        public_slug: normalizePublicSlug(publicSlug),
        title: title.trim(),
        description: description.trim(),
        run_error_message: runErrorMessage.trim(),
      },
      {
        onSuccess: () => {
          notifications.show({ message: 'Portal settings saved', color: 'blue' });
        },
        onError: (error) => {
          notifications.show({ message: getPortalSettingsErrorMessage(error), color: 'red' });
        },
      },
    );
  };

  const handleRegenerateLink = () => {
    regenerateLink(
      { automationId: automation.id },
      {
        onSuccess: () => {
          setConfirmRegenerateOpen(false);
          notifications.show({ message: 'Portal link regenerated', color: 'blue' });
        },
        onError: () => {
          notifications.show({ message: 'Could not regenerate the portal link', color: 'red' });
        },
      },
    );
  };

  const handleLogoSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;

    uploadLogo(
      { automationId: automation.id, file },
      {
        onSuccess: () => {
          notifications.show({ message: 'Portal logo uploaded', color: 'blue' });
        },
        onError: () => {
          notifications.show({ message: 'Could not upload the portal logo', color: 'red' });
        },
      },
    );
  };

  const handleRemoveLogo = () => {
    removeLogo(
      { automationId: automation.id },
      {
        onSuccess: () => {
          notifications.show({ message: 'Portal logo removed', color: 'blue' });
        },
        onError: () => {
          notifications.show({ message: 'Could not remove the portal logo', color: 'red' });
        },
      },
    );
  };

  return (
    <Box>
      {isLoading || !portal ? (
        <Stack gap="sm">
          <Skeleton height={28} radius="sm" />
          <Skeleton height={72} radius="sm" />
          <Skeleton height={40} radius="sm" />
        </Stack>
      ) : (
        <>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" gap="lg" wrap="nowrap">
              <Stack gap={2} style={{ flex: 1 }}>
                <Group gap="xs">
                  <IconWorldWww size={16} color="#757575" />
                  <Text size="13px" fw={500} c="#0F172A">
                    Public portal
                  </Text>
                </Group>
                <Text size="12px" c="#757575">
                  Anyone with the portal URL can submit a manual run and download pinned outputs
                  after the run completes.
                </Text>
              </Stack>
              <StyledSwitch
                checked={enabled}
                onChange={(event) => setEnabled(event.currentTarget.checked)}
                disabled={!portal.is_ready && !enabled}
                label={enabled ? 'Enabled' : 'Disabled'}
              />
            </Group>

            {!portal.is_ready && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="yellow"
                styles={{
                  root: { borderRadius: 6, borderColor: '#FDE68A' },
                  message: { color: '#854D0E', fontSize: '12px' },
                }}
              >
                {portal.readiness_error}
              </Alert>
            )}

            <Stack gap="sm">
              <StyledTextInput
                label="Portal title"
                value={title}
                onChange={(event) => setTitle(event.currentTarget.value)}
                maxLength={PORTAL_TITLE_MAX_LENGTH}
                placeholder={automation.name}
              />
              <StyledTextarea
                label="Portal description"
                value={description}
                onChange={(event) => setDescription(event.currentTarget.value)}
                minRows={3}
                maxRows={5}
                maxLength={PORTAL_DESCRIPTION_MAX_LENGTH}
                placeholder="Describe what users should submit."
              />
              <StyledTextarea
                label="Run error message"
                value={runErrorMessage}
                onChange={(event) => setRunErrorMessage(event.currentTarget.value)}
                minRows={3}
                maxRows={5}
                maxLength={PORTAL_RUN_ERROR_MESSAGE_MAX_LENGTH}
                placeholder="This request could not be completed. Please contact the team that shared this portal."
              />
              <StyledTextInput
                label="Portal slug"
                value={publicSlug}
                onChange={(event) => setPublicSlug(event.currentTarget.value)}
                maxLength={PORTAL_SLUG_MAX_LENGTH}
                placeholder="customer-uploads"
                description="Lowercase letters, numbers, and hyphens."
                error={publicSlugValidationError}
              />
              <Group justify="flex-end">
                <StyledButton
                  onClick={handleSave}
                  disabled={!canSave}
                  loading={isUpdatingPortal}
                  loaderProps={{ color: 'white' }}
                >
                  Save
                </StyledButton>
              </Group>
            </Stack>

            <Divider style={{ borderColor: '#ECECED' }} />

            <Stack gap="xs">
              <Text size="13px" fw={500} c="#0F172A">
                Branding
              </Text>
              <Text size="12px" c="#757575">
                Add a customer logo to the public portal page.
              </Text>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleLogoSelected}
                style={{ display: 'none' }}
                aria-label="Portal logo file"
              />
              <Group gap="xs">
                <StyledButton
                  variant="outline"
                  size="sm"
                  leftSection={<IconUpload size={14} />}
                  onClick={() => logoInputRef.current?.click()}
                  loading={isUploadingLogo}
                >
                  {hasLogo ? 'Replace logo' : 'Upload logo'}
                </StyledButton>
                <StyledButton
                  variant="outline"
                  size="sm"
                  leftSection={<IconTrash size={14} />}
                  onClick={handleRemoveLogo}
                  loading={isRemovingLogo}
                  disabled={!hasLogo}
                >
                  Remove
                </StyledButton>
              </Group>
              {portal.logo_url ? (
                <Box
                  px="sm"
                  py="xs"
                  style={{
                    alignItems: 'center',
                    border: '1px solid #ECECED',
                    borderRadius: 6,
                    display: 'flex',
                    height: 72,
                    justifyContent: 'center',
                    maxWidth: 220,
                    background: '#FFFFFF',
                  }}
                >
                  <img
                    src={portal.logo_url}
                    alt="Portal logo preview"
                    style={{
                      display: 'block',
                      maxHeight: 48,
                      maxWidth: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              ) : (
                <Text size="12px" c="#757575">
                  {hasLogo ? 'Logo uploaded. Preview unavailable.' : 'No logo uploaded.'}
                </Text>
              )}
            </Stack>

            <Divider style={{ borderColor: '#ECECED' }} />

            <Stack gap="xs">
              <Text size="13px" fw={500} c="#0F172A">
                Portal link
              </Text>
              <Box
                px="sm"
                py={8}
                style={{
                  border: '1px solid #ECECED',
                  borderRadius: 6,
                  background: enabled ? '#FFFFFF' : '#FAFAFA',
                }}
              >
                <Text size="12px" c={enabled ? '#0F172A' : '#757575'} truncate>
                  {publicUrl}
                </Text>
              </Box>
              <Group gap="xs">
                {!confirmRegenerateOpen && (
                  <>
                    <StyledButton
                      variant="outline"
                      size="sm"
                      leftSection={<IconCopy size={14} />}
                      onClick={handleCopyUrl}
                      disabled={!enabled || !publicUrl}
                    >
                      Copy
                    </StyledButton>
                    <StyledButton
                      variant="outline"
                      size="sm"
                      leftSection={<IconExternalLink size={14} />}
                      onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
                      disabled={!enabled || !publicUrl}
                    >
                      Open
                    </StyledButton>
                    <StyledButton
                      variant="outline"
                      size="sm"
                      leftSection={<IconRefresh size={14} />}
                      onClick={() => setConfirmRegenerateOpen(true)}
                      disabled={!publicUrl}
                    >
                      Regenerate
                    </StyledButton>
                  </>
                )}
              </Group>
              {confirmRegenerateOpen ? (
                <Alert
                  color="red"
                  icon={<IconAlertTriangle size={16} />}
                  styles={{
                    root: { borderRadius: 6, borderColor: '#FCA5A5' },
                    message: { color: '#7F1D1D', fontSize: '12px' },
                  }}
                >
                  <Stack gap="xs">
                    <Text size="12px" fw={500} c="#7F1D1D">
                      Regenerate portal link?
                    </Text>
                    <Text size="12px" c="#7F1D1D">
                      {hasPublicSlug
                        ? 'This will invalidate token-based portal links. The slug link will remain active until the slug is changed or cleared.'
                        : 'This will immediately invalidate the current public portal link. Anyone using the old URL will no longer be able to open the portal or view portal-run results.'}
                    </Text>
                    <Group gap="xs">
                      <StyledButton
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmRegenerateOpen(false)}
                      >
                        Cancel
                      </StyledButton>
                      <StyledButton
                        variant="delete"
                        size="sm"
                        onClick={handleRegenerateLink}
                        loading={isRegeneratingLink}
                      >
                        Regenerate
                      </StyledButton>
                    </Group>
                  </Stack>
                </Alert>
              ) : (
                <Text size="12px" c="#757575">
                  {hasPublicSlug
                    ? 'Regenerating changes the token URL. The slug URL remains active until the slug is changed or cleared.'
                    : 'Regenerating changes the public URL and immediately invalidates the previous link.'}
                </Text>
              )}
            </Stack>

            <Divider style={{ borderColor: '#ECECED' }} />

            <Stack gap="xs">
              <Text size="13px" fw={500} c="#0F172A">
                Recent portal runs
              </Text>
              {isLoadingPortalRuns ? (
                <Stack gap={6}>
                  <Skeleton height={34} radius="sm" />
                  <Skeleton height={34} radius="sm" />
                </Stack>
              ) : recentPortalRuns.length === 0 ? (
                <Text size="12px" c="#757575">
                  No portal runs yet.
                </Text>
              ) : (
                <Stack gap={0}>
                  {recentPortalRuns.map((portalRun) => (
                    <AutomationPortalRunRow
                      key={portalRun.public_run_token}
                      automationId={automation.id}
                      portalRun={portalRun}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Box>
  );
};

function getPortalSettingsErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object' && 'detail' in data) {
      return getReadableErrorDetail(data.detail) || 'Could not save portal settings';
    }
  }
  return 'Could not save portal settings';
}

function getReadableErrorDetail(detail: unknown): string | null {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => getReadableErrorDetail(item))
      .filter((message): message is string => !!message);
    return messages.length > 0 ? messages.join(', ') : null;
  }
  if (detail && typeof detail === 'object') {
    if ('msg' in detail && typeof detail.msg === 'string') return detail.msg;
    if ('message' in detail && typeof detail.message === 'string') return detail.message;
    if ('detail' in detail) return getReadableErrorDetail(detail.detail);
  }
  return null;
}
